import { pick } from 'lodash';
import { Ref, computed, onMounted, ref, watch } from 'vue';

import { SimpleApiInterface } from 'SharedDir/backend/api/classes/simpleApi';
import { useConfirmationDialog } from 'SharedDir/common/composables/useConfirmationDialog';
import { useLoading } from 'SharedDir/common/composables/useLoading';
import { useNotifications } from 'SharedDir/common/composables/useNotifications';
import { usePagination } from 'SharedDir/common/composables/usePagination';
import {
  AbstractQueryParams,
  ParamsSet,
  QueryStringUsageOptions,
  useQueryString,
} from 'SharedDir/common/composables/useQueryString';
import { MakeRequestOptions, makeRequest } from 'SharedDir/common/composables/useRequests';
import { MESSAGE_FORM_DELETE_SUCCESS } from 'SharedDir/constants/messages';
import { BasePagination } from 'SharedDir/declarations/pagination';
import { HasId, QuasarTableExportColumns } from 'SharedDir/declarations/shared';
import fileDownloader from 'SharedDir/utils/file-downloader';
import { removeRecordFromList } from 'SharedDir/utils/helpers';

export type UseFilterParams<Filter, Sanitizer extends AbstractQueryParams> = () => {
  filterParams: Ref<Filter>;
  defaultFilterParams: () => Filter;
  sanitizeQueryFilterParams: () => Sanitizer;
};

interface ListOptions extends QueryStringUsageOptions {
  useSkeleton: boolean;
  useQueryString: boolean;
}

const defaultOptions: ListOptions = {
  useSkeleton: false,
  saveInStore: true,
  useQueryString: true,
};

export function useList<
  FilterSanitizer extends AbstractQueryParams,
  Filter extends ParamsSet<FilterSanitizer>,
  TSingle extends HasId<'id', string | number>,
  TStore,
  TUpdate = TStore,
  TPaginated extends HasId<'id', string | number> = TSingle,
  TStoreResult extends HasId<'id', string | number> = TSingle,
  TUpdateResult extends HasId<'id', string | number> = TSingle,
  TDeleteResult extends HasId<'id', string | number> = TSingle
>(
  useFilter: UseFilterParams<Filter, FilterSanitizer>,
  classApi: SimpleApiInterface<
    TSingle,
    TStore,
    TUpdate,
    TPaginated,
    TStoreResult,
    TUpdateResult,
    TDeleteResult
  >,
  useSkeleton?: boolean,
  basePagination?: Partial<BasePagination>,
  opt?: Partial<ListOptions>
) {
  const innerOpt = { ...defaultOptions, ...opt };

  const list = ref([]) as Ref<TPaginated[]>;
  const { filterParams, defaultFilterParams, sanitizeQueryFilterParams } = useFilter();
  const {
    paginationParams,
    sanitizeQueryPagination,
    setPaginationFromData,
    defaultPaginator,
    resetPagination,
  } = usePagination(basePagination || {});
  const combinedParametersSanitizers = {
    ...sanitizeQueryFilterParams(),
    ...sanitizeQueryPagination(),
  };

  const combinedParameters = computed(() => ({ ...filterParams.value, ...paginationParams.value }));

  const { setQueryParams, fillFromQueryString, fillFromQueryStringSsr } = useQueryString(
    combinedParametersSanitizers,
    {
      ...filterParams.value,
      ...paginationParams.value,
    },
    {
      ...pick(innerOpt, 'saveInStore'),
    }
  );

  const receive = async (response: Awaited<ReturnType<typeof classApi.getPaginated>>) => {
    list.value = response.data;
    setPaginationFromData(response);
    if (innerOpt?.useQueryString) {
      await setQueryParams({ ...paginationParams.value, ...filterParams.value });
    }
  };

  async function ajaxCall() {
    return classApi.getPaginated(combinedParameters.value);
  }

  async function getData() {
    await receive(await ajaxCall());
  }

  async function refreshList() {
    ({ data: list.value } = await ajaxCall());
  }

  const fetch = async () => {
    const options = useSkeleton
      ? {
          startLoading: () => {
            const { action } = useLoading();
            action.show();
          },
          finishLoading: () => {
            const { action } = useLoading();
            action.hide();
          },
        }
      : undefined;
    return makeRequest(getData, options);
  };

  /**
   * Вызывается при изменении параметров пагинации в таблице
   *
   * @param { pagination }
   */
  const onRequest = async ({ pagination: tablePagination }: { pagination: BasePagination }) => {
    setPaginationFromData(tablePagination);

    await fetch();
  };

  const clearParameters = async () => {
    paginationParams.value = defaultPaginator();
    filterParams.value = defaultFilterParams();

    await fetch();
  };

  const exportTableAll = async (data: QuasarTableExportColumns) => {
    const fileResponse = await classApi.exportTableAll(
      { ...paginationParams.value, ...filterParams.value },
      data
    );

    fileDownloader.saveFileFromResponse(fileResponse);
  };

  onMounted(() => {
    if (innerOpt?.useQueryString) {
      fillFromQueryString(paginationParams, filterParams);
    }
    watch(
      () => filterParams.value,
      () => {
        resetPagination();
      },
      { deep: true }
    );
  });

  const { withConfirmation } = useConfirmationDialog();
  const { successNotification } = useNotifications();

  async function remove(
    id: string | number,
    options: Partial<{
      confirmMessage: string;
      successMessage: string;
      requestOptions: Partial<MakeRequestOptions>;
      mode: 'refresh' | 'exclude';
      confirm: boolean;
    }> = {}
  ) {
    const {
      confirmMessage = 'Вы действительно хотите удалить запись?',
      mode = 'refresh',
      successMessage = MESSAGE_FORM_DELETE_SUCCESS,
      confirm = true,
    } = options;
    return new Promise((resolve, reject) => {
      async function justDoIt() {
        const response = await makeRequest(() => classApi.delete(id), options.requestOptions);
        successNotification(successMessage);
        if (mode === 'refresh') {
          await fetch();
        } else {
          removeRecordFromList(list.value, id);
        }
        resolve(response);
      }

      if (confirm) {
        withConfirmation({ message: confirmMessage }, justDoIt, reject);
      } else {
        justDoIt().catch(reject);
      }
    });
  }

  return {
    list,
    fetch,
    remove,
    getData,
    refreshList,
    ajaxCall,
    paginationParams,
    filterParams,
    onRequest,
    clearParameters,
    combinedParameters,
    setPaginationFromData,
    setQueryParams,
    defaultFilterParams,
    fillFromQueryString,
    fillFromQueryStringSsr,
    exportTableAll,
    resetPagination,
    receive,
  };
}
